import { useEffect, useState } from "react";
import AcceleratorDoc from "../../models/AcceleratorDoc";
import { WindowProxyStore } from "../../stores/impl/WindowProxyStore";
import { Hydratable } from "../../models/hydration/Hydratable";
import { CollapsiblePanel } from "../CollapsiblePanel";
import DocumentViewerHeaderButtons from "../DocumentViewerHeaderButtons";
import { ImageViewer } from "./ImageViewer";
import { RemoteImageViewer } from "./remote/RemoteImageViewer";
import { usePageDispatch, usePageState } from "../pageProvider/PageProvider";
import { useHighlightAllSpans } from "../hooks/useHighlightAllSpans";
import { useSpanHighlightState } from "../highlightProvider/SpanHighlightProvider";

type Props = {
  acceleratorDoc: Hydratable<AcceleratorDoc> | undefined;
  showSpanOutline: boolean;
  setSpanOutline: () => void;
};

const WINDOW_PROXY_STORE_KEY = "RemoteDocumentImageViewer";
const DocumentImageViewer = (props: Props) => {
  const acceleratorDoc = props.acceleratorDoc?.isHydrated()
    ? props.acceleratorDoc.getHydrated()
    : undefined;

  // Hook span higlighting from this image viewer
  useHighlightAllSpans(
    "____",
    "red",
    props.acceleratorDoc?._hydrated,
    props.showSpanOutline,
  );
  const spanHighlightState = useSpanHighlightState();

  const pageState = usePageState();
  const pageDispatch = usePageDispatch();
  const [poppedOut, setPoppedOut] = useState<boolean>(() => {
    return (
      WindowProxyStore.getWithoutLookup(WINDOW_PROXY_STORE_KEY) != undefined
    );
  });

  const [lastDocUuid, setLastDocUuid] = useState<string | null>(null);

  const changePageNumber = (newIndex: number) => {
    if (!acceleratorDoc) {
      pageDispatch(0);
      return;
    }
    if (newIndex == -1) {
      pageDispatch(acceleratorDoc.pages.length - 1);
      return;
    }
    pageDispatch(Math.min(acceleratorDoc.pages.length - 1, newIndex));
  };

  const shiftPageNumber = (increment: boolean) => {
    if (!increment && pageState.pageIndex == 0) {
      return;
    }
    changePageNumber(
      increment ? pageState.pageIndex + 1 : pageState.pageIndex - 1,
    );
  };

  useEffect(() => {
    if (props.acceleratorDoc?.isHydrated(false)) {
      if (lastDocUuid !== props.acceleratorDoc.getHydrated()!.uuid) {
        setLastDocUuid(props.acceleratorDoc.getHydrated()!.uuid);
      }
    } else {
      setLastDocUuid(null);
    }
  }, [acceleratorDoc]);

  useEffect(() => {
    // set to negative value if AcceleratorDoc is null
    if (!lastDocUuid) {
      pageDispatch(-1);
    } else {
      pageDispatch(0);
    }
  }, [lastDocUuid]);

  const showImageViewer =
    pageState.pageIndex > -1 && lastDocUuid && props.acceleratorDoc;

  const pageCount = acceleratorDoc?.pages.length;
  const title = showImageViewer
    ? `Page ${pageState.pageIndex + 1} of ${pageCount}`
    : "Document Viewer";

  const page = acceleratorDoc?.getPage(pageState.pageIndex);
  const pageId = page?.externalPageIdentifier;
  const pageDimensions = page
    ? {
        width: page.width,
        height: page.height,
      }
    : undefined;

  return (
    <CollapsiblePanel
      title={title}
      open
      sticky
      headerChildren={
        <DocumentViewerHeaderButtons
          showSpans={props.showSpanOutline}
          showSpansFn={props.setSpanOutline}
          changeIndex={changePageNumber}
          shiftPageByOne={shiftPageNumber}
          popOut={() => setPoppedOut(!poppedOut)}
          poppedOut={poppedOut}
          style={{
            float: "right",
            // half of bottom padding to be inline with text
            marginTop: "calc(-1.1rem / 2)",
          }}
        />
      }
    >
      <>
        {showImageViewer && (
          <ImageViewer
            imageUrl={`/v0/image/${lastDocUuid}/${pageId}`}
            pageIndex={pageState.pageIndex}
            pageDimensions={pageDimensions}
          />
        )}
        {poppedOut && (
          <RemoteImageViewer
            title={title}
            spanHighlightContext={spanHighlightState}
            windowProxyStoreKey={WINDOW_PROXY_STORE_KEY}
            totalPages={acceleratorDoc?.pages.length}
            showSpans={props.showSpanOutline}
            showSpansFn={props.setSpanOutline}
            shiftPageByOne={shiftPageNumber}
            changePageIndex={changePageNumber}
            pageIndex={pageState.pageIndex}
            pageDimensions={pageDimensions}
            imageUrl={`/v0/image/${lastDocUuid}/${pageId}`}
            close={() => {
              setPoppedOut(false);
              WindowProxyStore.close(WINDOW_PROXY_STORE_KEY);
            }}
          />
        )}
      </>
    </CollapsiblePanel>
  );
};

export { DocumentImageViewer };
